import {Component, DestroyRef, inject, Input} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AgGridAngular} from 'ag-grid-angular';
import {ColDef, FirstDataRenderedEvent, GridSizeChangedEvent, PostSortRowsParams} from 'ag-grid-community';
import {DatePipe} from '@angular/common';
import {GeographyService} from '../../../services/geography.service';
import {ErrorHandlingService} from '../../../services/error-handling.service';
import {IRegionTaxRate} from '../../../interfaces/IRegionTaxRate';
import {UIStateEnum} from '../../../enum/UIStateEnum';
import {RegionTaxRateStatus} from '../../../enum/RegionTaxRateStatus';
import {RegionTaxRateActionsComponent, IRegionTaxRateActionsContext} from './region-tax-rate-actions/region-tax-rate-actions.component';

@Component({
  selector: 'app-region-tax-rate-history',
  standalone: true,
  imports: [
    AgGridAngular
  ],
  templateUrl: './region-tax-rate-history.component.html',
  styleUrl: './region-tax-rate-history.component.css'
})
export class RegionTaxRateHistoryComponent {

  private geographyService = inject(GeographyService);
  private errorHandlingService = inject(ErrorHandlingService);
  private destroyRef = inject(DestroyRef);
  private datePipe = inject(DatePipe);

  public uiState: UIStateEnum = UIStateEnum.ShowLoading;
  public uiStateEnum = UIStateEnum;
  public taxRates: IRegionTaxRate[] = [];

  public gridContext: IRegionTaxRateActionsContext = {
    onCancelSuccess: () => this.reload()
  };

  public get hasScheduledRate(): boolean {
    return this.taxRates.some(r => r.Status === RegionTaxRateStatus.Scheduled);
  }

  public columnDefs: ColDef[] = [
    {
      headerName: 'Status',
      width: 120,
      cellRenderer: (params: any) => {
        switch (params.data.Status as RegionTaxRateStatus) {
          case RegionTaxRateStatus.Active:
            return `<span class="badge bg-success">Active</span>`;
          case RegionTaxRateStatus.Scheduled:
            return `<span class="badge bg-warning text-dark">Scheduled</span>`;
          case RegionTaxRateStatus.Cancelled:
            return `<span class="badge bg-danger">Cancelled</span>`;
          case RegionTaxRateStatus.Past:
            return `<span class="badge bg-secondary">Past</span>`;
          default:
            return `<span class="badge bg-secondary">Unknown</span>`;
        }
      }
    },
    {
      headerName: 'GST (%)',
      field: 'GstRate',
      sortable: true,
      resizable: true,
      valueFormatter: params => (Math.round(params.value * 100000) / 1000).toString()
    },
    {
      headerName: 'PST/QST (%)',
      field: 'PstRate',
      sortable: true,
      resizable: true,
      valueFormatter: params => (Math.round(params.value * 100000) / 1000).toString()
    },
    {
      headerName: 'HST (%)',
      field: 'HstRate',
      sortable: true,
      resizable: true,
      valueFormatter: params => (Math.round(params.value * 100000) / 1000).toString()
    },
    {
      headerName: 'Start Date',
      field: 'StartDate',
      sortable: true,
      resizable: true,
      valueFormatter: params => this.datePipe.transform(params.value, 'MMM d, y', 'UTC') ?? ''
    },
    {
      headerName: 'End Date',
      field: 'EndDate',
      sortable: true,
      resizable: true,
      valueFormatter: params => params.value ? (this.datePipe.transform(params.value, 'MMM d, y', 'UTC') ?? '') : '—'
    },
    {
      headerName: 'Created On',
      field: 'CreatedOn',
      sortable: true,
      resizable: true,
      valueFormatter: params => this.datePipe.transform(params.value, 'MMM d, y') ?? ''
    },
    {
      headerName: '',
      width: 60,
      cellRenderer: RegionTaxRateActionsComponent,
      sortable: false,
      resizable: false
    }
  ];

  private currentRegionId: string | null = null;

  @Input()
  public set regionId(regionIdP: string | null) {
    if (regionIdP) {
      this.currentRegionId = regionIdP;
      this.loadHistory(regionIdP);
    }
  }

  public reload(): void {
    if (this.currentRegionId) {
      this.loadHistory(this.currentRegionId);
    }
  }

  public postSortRows = (paramsP: PostSortRowsParams): void => {
    const priority = (data: IRegionTaxRate): number => {
      switch (data.Status) {
        case RegionTaxRateStatus.Scheduled: return 3;
        case RegionTaxRateStatus.Active: return 2;
        case RegionTaxRateStatus.Past: return 1;
        default: return 0; // Cancelled and unknown sort last
      }
    };

    paramsP.nodes.sort((a, b) => {
      const priorityDiff = priority(b.data!) - priority(a.data!);
      if (priorityDiff !== 0) return priorityDiff;
      return new Date(b.data!.StartDate).getTime() - new Date(a.data!.StartDate).getTime();
    });
  }

  public onFirstDataRendered(paramsP: FirstDataRenderedEvent): void {
    paramsP.api.sizeColumnsToFit();
  }

  public onGridSizeChanged(paramsP: GridSizeChangedEvent): void {
    paramsP.api.sizeColumnsToFit();
  }

  private loadHistory(regionIdP: string): void {
    this.uiState = UIStateEnum.ShowLoading;
    this.geographyService.getTaxRatesForRegion(regionIdP).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (taxRates: IRegionTaxRate[]) => {
        this.taxRates = taxRates;
        this.uiState = UIStateEnum.ShowData;
      },
      error: (err) => {
        this.uiState = UIStateEnum.ShowData;
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    });
  }
}
